import type { FinanceData, FinancialLine } from '../domain/types';

const actualMonths = [
  '2026-01',
  '2026-02',
  '2026-03',
  '2026-04',
  '2026-05',
  '2026-06',
];
const forecastMonths = [
  '2026-07',
  '2026-08',
  '2026-09',
  '2026-10',
  '2026-11',
  '2026-12',
  '2027-01',
  '2027-02',
  '2027-03',
  '2027-04',
  '2027-05',
  '2027-06',
];
const allMonths = [...actualMonths, ...forecastMonths];

const monthlyAmounts = (
  actual: number,
  forecast: number,
  growth = 0,
): Record<string, number> =>
  Object.fromEntries(
    allMonths.map((month, index) => {
      const isForecast = index >= actualMonths.length;
      const forecastIndex = Math.max(index - actualMonths.length, 0);
      const seasonal = 1 + Math.sin(index * 1.4) * 0.025;
      return [
        month,
        Math.round(
          (isForecast ? forecast * (1 + growth * forecastIndex / 12) : actual) * seasonal,
        ),
      ];
    }),
  );

const line = (
  id: string,
  name: string,
  category: string,
  actual: number,
  forecast: number,
  source: string,
  controllability: FinancialLine['controllability'],
  growth = 0,
): FinancialLine => ({
  id,
  name,
  category,
  amounts: monthlyAmounts(actual, forecast, growth),
  source,
  sourceDetail: 'Synthetic demonstration data — replace with authenticated API data.',
  confidence: 'low',
  controllability,
});

/**
 * Synthetic data for local interface development only. It deliberately does not
 * contain values copied from the workbook or QuickBooks.
 */
export const createDemoFinanceData = (): FinanceData => {
  const data = {
  company: 'Deverus — demo workspace',
  currency: 'USD',
  asOfMonth: '2026-06',
  dataStatus: 'sample',
  lastSyncedAt: new Date(2026, 5, 30, 12).toISOString(),
  months: [
    ...actualMonths.map((month) => ({ month, kind: 'actual' as const })),
    ...forecastMonths.map((month) => ({ month, kind: 'forecast' as const })),
  ],
  revenueLines: [
    line('rev-platform', 'Platform subscriptions', 'Recurring revenue', 196000, 205000, 'Demo', 'low', 0.08),
    line('rev-screening', 'Screening transactions', 'Usage revenue', 92000, 98000, 'Demo', 'low', 0.06),
    line('rev-services', 'Implementation & services', 'Services', 42000, 38000, 'Demo', 'medium', -0.02),
    line('rev-partner', 'Partner revenue share', 'Other revenue', 18000, 19000, 'Demo', 'low', 0.03),
  ],
  expenseLines: [
    line('exp-payroll-eng', 'Engineering payroll', 'People', 64000, 66000, 'Demo', 'committed', 0.04),
    line('exp-payroll-ops', 'Operations payroll', 'People', 47000, 48500, 'Demo', 'committed', 0.03),
    line('exp-payroll-sales', 'Sales payroll & commissions', 'People', 35000, 37000, 'Demo', 'medium', 0.08),
    line('exp-hosting', 'Hosting & infrastructure', 'Technology', 18500, 19800, 'Demo', 'medium', 0.06),
    line('exp-software', 'Software subscriptions', 'Technology', 14300, 14800, 'Demo', 'high', 0.04),
    line('exp-contractors', 'Contractors', 'Outside services', 22500, 21000, 'Demo', 'high', -0.05),
    line('exp-legal', 'Legal & professional fees', 'Professional services', 9800, 9000, 'Demo', 'medium', 0),
    line('exp-marketing', 'Marketing programs', 'Sales & marketing', 12000, 13500, 'Demo', 'high', 0.08),
    line('exp-office', 'Office & facilities', 'Facilities', 7600, 7600, 'Demo', 'medium', 0.02),
    line('exp-insurance', 'Insurance', 'Corporate', 6800, 6900, 'Demo', 'low', 0.03),
    line('exp-travel', 'Travel & entertainment', 'Corporate', 5900, 6200, 'Demo', 'high', 0.05),
    line('exp-bank', 'Bank, merchant & interest fees', 'Corporate', 11200, 11600, 'Demo', 'low', 0.01),
  ],
  startingForecastCash: 415000,
  baselineMonthlyOwnerDraw: 62000,
  baselineOtherCashFlow: Object.fromEntries(forecastMonths.map((month) => [month, -6000])),
  arAging: [
    { label: 'Current', amount: 430000, recoverable: false },
    { label: '1–30 days', amount: 118000, recoverable: false },
    { label: '31–60 days', amount: 92000, recoverable: false },
    { label: '61–90 days', amount: 76000, recoverable: true },
    { label: '90+ days', amount: 235000, recoverable: true },
  ],
  debts: [
    { id: 'debt-loc', name: 'Operating line of credit', balance: 620000, annualRate: 0.0875, scheduledMonthlyPrincipal: 6500, source: 'Demo' },
    { id: 'debt-term', name: 'Term loan', balance: 390000, annualRate: 0.0725, scheduledMonthlyPrincipal: 7800, source: 'Demo' },
    { id: 'debt-growth', name: 'Growth facility', balance: 245000, annualRate: null, scheduledMonthlyPrincipal: 5000, source: 'Demo' },
  ],
  assumptions: [
    {
      id: 'assumption-forecast-source',
      name: 'Forecast source',
      description: 'Monthly forecast imported from the planning workbook.',
      value: 'Planning workbook',
      unit: 'text',
      type: 'forecast',
      source: 'Demo',
      sourceDetail: 'Synthetic development record.',
      confidence: 'low',
      editable: false,
    },
    {
      id: 'assumption-actual-lock',
      name: 'Actual periods are locked',
      description: 'Scenario levers apply only to forecast months.',
      value: 'Yes',
      unit: 'text',
      type: 'accounting',
      source: 'Model policy',
      sourceDetail: 'Scenario engine control.',
      confidence: 'high',
      editable: false,
    },
    {
      id: 'assumption-owner-draw',
      name: 'Monthly owner distributions',
      description: 'Cash distributed outside operating expenses.',
      value: 62000,
      unit: 'currency',
      type: 'scenario',
      source: 'Demo',
      sourceDetail: 'Editable in Cash, AR & Debt.',
      confidence: 'low',
      editable: true,
      relatedId: 'ownerDrawMonthly',
    },
    {
      id: 'assumption-ar-recovery',
      name: 'Past-due AR recovery',
      description: 'Share of 61+ day receivables collected incrementally.',
      value: 0,
      unit: 'percent',
      type: 'scenario',
      source: 'User scenario',
      sourceDetail: 'Editable in Cash, AR & Debt.',
      confidence: 'medium',
      editable: true,
      relatedId: 'arRecoveryPercent',
    },
  ],
  } satisfies Omit<FinanceData, 'controlTotals'>;
  const years = [...new Set(data.months.map(({ month }) => month.slice(0, 4)))];
  const controlTotals = Object.fromEntries(years.map((year) => {
    const months = data.months.filter(({ month }) => month.startsWith(`${year}-`));
    const revenue = data.revenueLines.reduce(
      (total, revenueLine) => total + months.reduce((subtotal, { month }) => subtotal + revenueLine.amounts[month], 0),
      0,
    );
    const expenses = data.expenseLines.reduce(
      (total, expenseLine) => total + months.reduce((subtotal, { month }) => subtotal + expenseLine.amounts[month], 0),
      0,
    );
    return [year, { revenue, expenses, profit: revenue - expenses }];
  }));
  return { ...data, controlTotals };
};
